import * as Ramda from 'ramda';
import type { valueOf } from '../common/utility';

export const primePlus = 'PRIME_PLUS';
export const prime_tech_plus = 'PRIME_TECH_PLUS';
export const p_one_plus = 'P_ONE_PLUS';
export const prime_one_plus_us = 'PRIME_ONE_PLUS_US';
export const prime_one_tech_plus = 'PRIME_ONE_TECH_PLUS';

export * from './product-suite.enums';

export const ProductTypes = {
  p100: 'P100',
  p250: 'P250',
  // The Rater X Prime 250. Same offering to a broker as p250; a different key
  // only so the two rating frameworks can coexist through the cutover.
  p250x: 'P250X',
  notSupported: 'NotSupported',
  p250S: 'P250_S',
  p100_pro: 'P100_PRO',
  prime_one: 'P_ONE',
  prime_one_au: 'PRIME_ONE_AU',
  prime_one_us: 'PRIME_ONE_US',
  p_plus: 'P_PLUS',
  prime_tech_plus,
  prime_tech: 'PRIME_TECH',
  p_one_plus,
  prime_one_tech: 'PRIME_ONE_TECH',
  prime_one_tech_plus,
  prime_one_plus_us,
  zsp: 'ZSP',
} as const;

export type ProductType = (typeof ProductTypes)[keyof typeof ProductTypes];

export const isSurplusProduct = (productType: ProductType) => {
  return [
    ProductTypes.p250,
    ProductTypes.p250x,
    ProductTypes.prime_tech,
    ProductTypes.prime_one_us,
    // Excess sits on the same paper as its primary, so Prime One Plus US is
    // surplus like the Prime One US it attaches over.
    ProductTypes.prime_one_plus_us,
  ].some(Ramda.equals(productType));
};

export const PrimeXProductTypes = {
  p250x: ProductTypes.p250x,
  p100_pro: ProductTypes.p100_pro,
  prime_one: ProductTypes.prime_one,
  prime_tech: ProductTypes.prime_tech,
  prime_one_tech: ProductTypes.prime_one_tech,
  zsp: ProductTypes.zsp,
  prime_one_au: ProductTypes.prime_one_au,
  prime_one_us: ProductTypes.prime_one_us,
  prime_one_plus_us: ProductTypes.prime_one_plus_us,
} as const satisfies Partial<typeof ProductTypes>;

export const RATER_X_PRODUCT_TYPES = {
  p250x: ProductTypes.p250x,
  prime_tech: ProductTypes.prime_tech,
  p100_pro: ProductTypes.p100_pro,
  prime_one: ProductTypes.prime_one,
  prime_one_tech: ProductTypes.prime_one_tech,
  zsp: ProductTypes.zsp,
  prime_one_au: ProductTypes.prime_one_au,
  prime_one_us: ProductTypes.prime_one_us,
};

export type PrimeXProductType =
  (typeof ProductTypes)[keyof typeof PrimeXProductTypes];

export const isPrimeX = (product: string) =>
  Object.values(PrimeXProductTypes).some(Ramda.equals(product));

export const isRaterXProduct = (productType: string) => {
  return Object.values(RATER_X_PRODUCT_TYPES).some(Ramda.equals(productType));
};

export const isLedgerStyleProduct = (productType: ProductType) => {
  return isPrimeX(productType);
};

export const isEndorsableProductType = (product: string) =>
  [
    ProductTypes.p100,
    ProductTypes.p250,
    ProductTypes.p100_pro,
    ProductTypes.prime_one,
  ].some(Ramda.equals(product));

export const hasSupplementalFirmo = (productType: ProductType) => {
  return [ProductTypes.prime_tech, ProductTypes.prime_one_tech].some(
    Ramda.equals(productType)
  );
};

export const isZurichProduct = (productType: ProductType | string) => {
  return productType === ProductTypes.zsp;
};

/** P100, P250 and an unsupported product: the products on the legacy quote and policy flows, not Prime X. */
export const determineIsLegacyProduct = (product: ProductType) => {
  return [
    ProductTypes.p100,
    ProductTypes.p250,
    ProductTypes.notSupported,
  ].includes(product as any);
};

export const canAgencyCharge = (productType: ProductType) => {
  return [ProductTypes.p100_pro, ProductTypes.zsp].some(
    Ramda.equals(productType)
  );
};

export enum ProductLinesOfBusiness {
  CYBER = 'Cyber',
  TECH_ENO = 'Tech E&O',
  MANAGEMENT_LIABILITY = 'Management Liability',
}

export const EXCESS_FLOW = {
  primePlus,
  p_one_plus,
  prime_tech_plus,
  prime_one_tech_plus,
} as const;

export enum EXCESS_POLICY_TITLE {
  PRIME_PLUS = 'Prime Plus',
  PRIME_TECH_PLUS = 'Prime Tech Plus',
  P_ONE_PLUS = 'Prime One Plus',
  PRIME_ONE_TECH_PLUS = 'Prime One Tech Plus',
}

export type ExcessFlow = valueOf<typeof EXCESS_FLOW>;

export const isExcessProduct = (productType: string) =>
  Object.values(EXCESS_FLOW).some(Ramda.equals(productType));

// The primary rater whose premium is shown as an informational reference
// alongside each excess product's price. Keeps the excess→primary correspondence
// as data rather than inline conditionals.
export const PRIMARY_REFERENCE_RATER_BY_EXCESS = {
  [ProductTypes.prime_one_plus_us]: ProductTypes.prime_one_us,
} as const satisfies Partial<Record<PrimeXProductType, PrimeXProductType>>;

export const getPrimaryReferenceRater = (
  excessProduct: string
): PrimeXProductType | undefined =>
  PRIMARY_REFERENCE_RATER_BY_EXCESS[
    excessProduct as keyof typeof PRIMARY_REFERENCE_RATER_BY_EXCESS
  ];
